/**
 * Stream-Specific Theme Provider
 *
 * Creates distinct, beautiful experiences for each user stream while maintaining
 * brand consistency. Each stream has its own color palette, typography, and
 * visual design language.
 */

import { useMyStreamAndRole } from "@/hooks/useMyStreamAndRole";
import React, { createContext, ReactNode, useContext } from "react";

// ============================================================================
// STREAM THEME DEFINITIONS
// ============================================================================

export type StreamType =
    | "individual"
    | "family"
    | "business"
    | "assisted_care"
    | "admin";

export interface StreamTheme {
    // Core Identity
    name: string;
    description: string;
    icon: string;

    // Color Palette
    colors: {
        primary: string;
        secondary: string;
        accent: string;
        background: string;
        surface: string;
        text: {
            primary: string;
            secondary: string;
            muted: string;
        };
        border: string;
        success: string;
        warning: string;
        error: string;
        info: string;
        // Dark mode colors
        dark?: {
            background: string;
            surface: string;
            text: {
                primary: string;
                secondary: string;
                muted: string;
            };
            border: string;
        };
    };

    // Typography
    typography: {
        fontFamily: string;
        headingScale: {
            h1: string;
            h2: string;
            h3: string;
            h4: string;
            h5: string;
            h6: string;
        };
        bodyScale: {
            large: string;
            base: string;
            small: string;
            tiny: string;
        };
        weight: {
            light: number;
            normal: number;
            medium: number;
            semibold: number;
            bold: number;
        };
    };

    // Spacing & Layout
    spacing: {
        xs: string;
        sm: string;
        md: string;
        lg: string;
        xl: string;
        xxl: string;
    };

    // Component Styles
    components: {
        borderRadius: {
            sm: string;
            md: string;
            lg: string;
            xl: string;
        };
        shadows: {
            sm: string;
            md: string;
            lg: string;
            xl: string;
        };
        animations: {
            duration: {
                fast: string;
                normal: string;
                slow: string;
            };
            easing: string;
        };
    };

    // Stream-Specific Features
    features: {
        gamification: boolean;
        analytics: boolean;
        accessibility: boolean;
        minimalism: boolean;
    };

    // Accessibility
    accessibility: {
        highContrast: boolean;
        reducedMotion: boolean;
        largeText: boolean;
        focusVisible: boolean;
    };
}

// ============================================================================
// STREAM THEMES
// ============================================================================

const streamThemes: Record<StreamType, StreamTheme> = {
    individual: {
        name: "Individual",
        description: "Personal productivity and focus",
        icon: "🎯",

        colors: {
            primary: "#F59E0B", // Warm amber
            secondary: "#F97316", // Orange
            accent: "#FCD34D", // Light amber
            background: "#FFFBEB", // Warm cream
            surface: "#FFFFFF",
            text: {
                primary: "#92400E", // Dark amber
                secondary: "#B45309", // Medium amber
                muted: "#A16207", // Muted amber
            },
            border: "#FDE68A", // Light amber border
            success: "#10B981",
            warning: "#F59E0B",
            error: "#EF4444",
            info: "#3B82F6",
            // Dark mode colors
            dark: {
                background: "#1F2937", // Dark gray
                surface: "#374151", // Medium gray
                text: {
                    primary: "#F9FAFB", // Light gray
                    secondary: "#D1D5DB", // Medium light gray
                    muted: "#9CA3AF", // Muted gray
                },
                border: "#4B5563", // Dark border
            },
        },

        typography: {
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            headingScale: {
                h1: "2.5rem",
                h2: "2rem",
                h3: "1.75rem",
                h4: "1.5rem",
                h5: "1.25rem",
                h6: "1.125rem",
            },
            bodyScale: {
                large: "1.125rem",
                base: "1rem",
                small: "0.875rem",
                tiny: "0.75rem",
            },
            weight: {
                light: 300,
                normal: 400,
                medium: 500,
                semibold: 600,
                bold: 700,
            },
        },

        spacing: {
            xs: "0.25rem",
            sm: "0.5rem",
            md: "1rem",
            lg: "1.5rem",
            xl: "2rem",
            xxl: "3rem",
        },

        components: {
            borderRadius: {
                sm: "0.375rem",
                md: "0.5rem",
                lg: "0.75rem",
                xl: "1rem",
            },
            shadows: {
                sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                md: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            },
            animations: {
                duration: {
                    fast: "150ms",
                    normal: "300ms",
                    slow: "500ms",
                },
                easing: "cubic-bezier(0.4, 0, 0.2, 1)",
            },
        },

        features: {
            gamification: false,
            analytics: false,
            accessibility: true,
            minimalism: true,
        },

        accessibility: {
            highContrast: false,
            reducedMotion: false,
            largeText: false,
            focusVisible: true,
        },
    },

    family: {
        name: "Family",
        description: "Parent-child collaboration and fun",
        icon: "👨‍👩‍👧‍👦",

        colors: {
            primary: "#8B5CF6", // Vibrant purple
            secondary: "#A855F7", // Light purple
            accent: "#C084FC", // Very light purple
            background: "#FAF5FF", // Light purple background
            surface: "#FFFFFF",
            text: {
                primary: "#581C87", // Dark purple
                secondary: "#7C3AED", // Medium purple
                muted: "#6B21A8", // Muted purple
            },
            border: "#DDD6FE", // Light purple border
            success: "#10B981",
            warning: "#F59E0B",
            error: "#EF4444",
            info: "#3B82F6",
            // Dark mode colors
            dark: {
                background: "#1F1B2E", // Dark purple-gray
                surface: "#2D1B69", // Medium purple
                text: {
                    primary: "#E9D5FF", // Light purple
                    secondary: "#C4B5FD", // Medium light purple
                    muted: "#A78BFA", // Muted purple
                },
                border: "#5B21B6", // Dark purple border
            },
        },

        typography: {
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            headingScale: {
                h1: "2.75rem",
                h2: "2.25rem",
                h3: "1.875rem",
                h4: "1.5rem",
                h5: "1.25rem",
                h6: "1.125rem",
            },
            bodyScale: {
                large: "1.125rem",
                base: "1rem",
                small: "0.875rem",
                tiny: "0.75rem",
            },
            weight: {
                light: 300,
                normal: 400,
                medium: 500,
                semibold: 600,
                bold: 700,
            },
        },

        spacing: {
            xs: "0.25rem",
            sm: "0.5rem",
            md: "1rem",
            lg: "1.5rem",
            xl: "2rem",
            xxl: "3rem",
        },

        components: {
            borderRadius: {
                sm: "0.5rem",
                md: "0.75rem",
                lg: "1rem",
                xl: "1.25rem",
            },
            shadows: {
                sm: "0 2px 4px 0 rgba(139, 92, 246, 0.1)",
                md: "0 4px 8px 0 rgba(139, 92, 246, 0.15)",
                lg: "0 8px 16px 0 rgba(139, 92, 246, 0.2)",
                xl: "0 16px 32px 0 rgba(139, 92, 246, 0.25)",
            },
            animations: {
                duration: {
                    fast: "200ms",
                    normal: "400ms",
                    slow: "600ms",
                },
                easing: "cubic-bezier(0.34, 1.56, 0.64, 1)",
            },
        },

        features: {
            gamification: true,
            analytics: false,
            accessibility: true,
            minimalism: false,
        },

        accessibility: {
            highContrast: false,
            reducedMotion: false,
            largeText: false,
            focusVisible: true,
        },
    },

    business: {
        name: "Business",
        description: "Professional team management and analytics",
        icon: "💼",

        colors: {
            primary: "#1E40AF", // Professional blue
            secondary: "#3B82F6", // Medium blue
            accent: "#60A5FA", // Light blue
            background: "#F8FAFC", // Cool gray background
            surface: "#FFFFFF",
            text: {
                primary: "#1E293B", // Dark slate
                secondary: "#475569", // Medium slate
                muted: "#64748B", // Muted slate
            },
            border: "#E2E8F0", // Light gray border
            success: "#059669",
            warning: "#D97706",
            error: "#DC2626",
            info: "#0284C7",
            // Dark mode colors
            dark: {
                background: "#0F172A", // Dark slate
                surface: "#1E293B", // Medium slate
                text: {
                    primary: "#F1F5F9", // Light slate
                    secondary: "#CBD5E1", // Medium light slate
                    muted: "#94A3B8", // Muted slate
                },
                border: "#334155", // Dark slate border
            },
        },

        typography: {
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            headingScale: {
                h1: "2.25rem",
                h2: "1.875rem",
                h3: "1.5rem",
                h4: "1.25rem",
                h5: "1.125rem",
                h6: "1rem",
            },
            bodyScale: {
                large: "1rem",
                base: "0.875rem",
                small: "0.75rem",
                tiny: "0.625rem",
            },
            weight: {
                light: 300,
                normal: 400,
                medium: 500,
                semibold: 600,
                bold: 700,
            },
        },

        spacing: {
            xs: "0.125rem",
            sm: "0.25rem",
            md: "0.5rem",
            lg: "1rem",
            xl: "1.5rem",
            xxl: "2rem",
        },

        components: {
            borderRadius: {
                sm: "0.25rem",
                md: "0.375rem",
                lg: "0.5rem",
                xl: "0.75rem",
            },
            shadows: {
                sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                md: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
                lg: "0 4px 8px 0 rgba(0, 0, 0, 0.15)",
                xl: "0 8px 16px 0 rgba(0, 0, 0, 0.2)",
            },
            animations: {
                duration: {
                    fast: "100ms",
                    normal: "200ms",
                    slow: "300ms",
                },
                easing: "cubic-bezier(0.4, 0, 0.2, 1)",
            },
        },

        features: {
            gamification: false,
            analytics: true,
            accessibility: true,
            minimalism: true,
        },

        accessibility: {
            highContrast: false,
            reducedMotion: true,
            largeText: false,
            focusVisible: true,
        },
    },

    assisted_care: {
        name: "Assisted Care",
        description: "Accessible health and wellness support",
        icon: "❤️",

        colors: {
            primary: "#059669", // Calming green
            secondary: "#10B981", // Medium green
            accent: "#34D399", // Light green
            background: "#F0FDF4", // Light green background
            surface: "#FFFFFF",
            text: {
                primary: "#064E3B", // Dark green
                secondary: "#065F46", // Medium green
                muted: "#047857", // Muted green
            },
            border: "#BBF7D0", // Light green border
            success: "#059669",
            warning: "#D97706",
            error: "#DC2626",
            info: "#0284C7",
            // Dark mode colors
            dark: {
                background: "#0F1B0F", // Dark green
                surface: "#1A2E1A", // Medium green
                text: {
                    primary: "#D1FAE5", // Light green
                    secondary: "#A7F3D0", // Medium light green
                    muted: "#6EE7B7", // Muted green
                },
                border: "#065F46", // Dark green border
            },
        },

        typography: {
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            headingScale: {
                h1: "3rem",
                h2: "2.5rem",
                h3: "2rem",
                h4: "1.75rem",
                h5: "1.5rem",
                h6: "1.25rem",
            },
            bodyScale: {
                large: "1.25rem",
                base: "1.125rem",
                small: "1rem",
                tiny: "0.875rem",
            },
            weight: {
                light: 300,
                normal: 400,
                medium: 500,
                semibold: 600,
                bold: 700,
            },
        },

        spacing: {
            xs: "0.5rem",
            sm: "0.75rem",
            md: "1.25rem",
            lg: "2rem",
            xl: "2.5rem",
            xxl: "4rem",
        },

        components: {
            borderRadius: {
                sm: "0.75rem",
                md: "1rem",
                lg: "1.25rem",
                xl: "1.5rem",
            },
            shadows: {
                sm: "0 2px 4px 0 rgba(5, 150, 105, 0.1)",
                md: "0 4px 8px 0 rgba(5, 150, 105, 0.15)",
                lg: "0 8px 16px 0 rgba(5, 150, 105, 0.2)",
                xl: "0 16px 32px 0 rgba(5, 150, 105, 0.25)",
            },
            animations: {
                duration: {
                    fast: "250ms",
                    normal: "500ms",
                    slow: "750ms",
                },
                easing: "cubic-bezier(0.4, 0, 0.2, 1)",
            },
        },

        features: {
            gamification: false,
            analytics: false,
            accessibility: true,
            minimalism: false,
        },

        accessibility: {
            highContrast: true,
            reducedMotion: true,
            largeText: true,
            focusVisible: true,
        },
    },

    admin: {
        name: "Admin",
        description: "System administration and support",
        icon: "⚙️",

        colors: {
            primary: "#DC2626", // Admin red
            secondary: "#EF4444", // Light red
            accent: "#FCA5A5", // Very light red
            background: "#FEF2F2", // Light red background
            surface: "#FFFFFF",
            text: {
                primary: "#991B1B", // Dark red
                secondary: "#B91C1C", // Medium red
                muted: "#7F1D1D", // Muted red
            },
            border: "#FECACA", // Light red border
            success: "#059669",
            warning: "#D97706",
            error: "#DC2626",
            info: "#0284C7",
            // Dark mode colors
            dark: {
                background: "#1F0F0F", // Dark red
                surface: "#2D1B1B", // Medium red
                text: {
                    primary: "#FEE2E2", // Light red
                    secondary: "#FECACA", // Medium light red
                    muted: "#FCA5A5", // Muted red
                },
                border: "#7F1D1D", // Dark red border
            },
        },

        typography: {
            fontFamily: "Inter, system-ui, -apple-system, sans-serif",
            headingScale: {
                h1: "2rem",
                h2: "1.75rem",
                h3: "1.5rem",
                h4: "1.25rem",
                h5: "1.125rem",
                h6: "1rem",
            },
            bodyScale: {
                large: "1rem",
                base: "0.875rem",
                small: "0.75rem",
                tiny: "0.625rem",
            },
            weight: {
                light: 300,
                normal: 400,
                medium: 500,
                semibold: 600,
                bold: 700,
            },
        },

        spacing: {
            xs: "0.125rem",
            sm: "0.25rem",
            md: "0.5rem",
            lg: "1rem",
            xl: "1.5rem",
            xxl: "2rem",
        },

        components: {
            borderRadius: {
                sm: "0.25rem",
                md: "0.375rem",
                lg: "0.5rem",
                xl: "0.75rem",
            },
            shadows: {
                sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                md: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
                lg: "0 4px 8px 0 rgba(0, 0, 0, 0.15)",
                xl: "0 8px 16px 0 rgba(0, 0, 0, 0.2)",
            },
            animations: {
                duration: {
                    fast: "100ms",
                    normal: "200ms",
                    slow: "300ms",
                },
                easing: "cubic-bezier(0.4, 0, 0.2, 1)",
            },
        },

        features: {
            gamification: false,
            analytics: true,
            accessibility: true,
            minimalism: true,
        },

        accessibility: {
            highContrast: true,
            reducedMotion: false,
            largeText: false,
            focusVisible: true,
        },
    },
};

// ============================================================================
// CONTEXT & PROVIDER
// ============================================================================

interface StreamThemeContextType {
    theme: StreamTheme;
    stream: StreamType;
    isDarkMode: boolean;
    toggleDarkMode: () => void;
    accessibilitySettings: {
        highContrast?: boolean;
        reducedMotion?: boolean;
        largeText?: boolean;
    };
    updateAccessibilitySettings: (
        settings: Partial<{
            highContrast?: boolean;
            reducedMotion?: boolean;
            largeText?: boolean;
        }>,
    ) => void;
}

const StreamThemeContext = createContext<StreamThemeContextType | null>(null);

interface StreamThemeProviderProps {
    children: ReactNode;
    forceStream?: StreamType;
    darkMode?: boolean;
    accessibility?: {
        highContrast?: boolean;
        reducedMotion?: boolean;
        largeText?: boolean;
    };
}

export const StreamThemeProvider: React.FC<StreamThemeProviderProps> = ({
    children,
    forceStream,
    darkMode = false,
    accessibility = {},
}) => {
    const { stream: userStream } = useMyStreamAndRole();
    const [isDarkMode, setIsDarkMode] = React.useState(darkMode);
    const [accessibilitySettings, setAccessibilitySettings] = React.useState(
        accessibility,
    );

    // Determine the active stream
    const activeStream: StreamType = forceStream || userStream || "individual";
    const theme = streamThemes[activeStream];

    const toggleDarkMode = () => setIsDarkMode(!isDarkMode);

    const updateAccessibilitySettings = (
        newSettings: Partial<typeof accessibilitySettings>,
    ) => {
        setAccessibilitySettings((prev) => ({ ...prev, ...newSettings }));
    };

    // Apply theme CSS variables to document root
    React.useEffect(() => {
        const root = document.documentElement;

        // Apply color variables (light or dark mode)
        const colorsToApply = isDarkMode && theme.colors.dark
            ? theme.colors.dark
            : theme.colors;

        Object.entries(colorsToApply).forEach(([key, value]) => {
            if (typeof value === "string") {
                root.style.setProperty(`--stream-${key}`, value);
            } else if (typeof value === "object" && key !== "dark") {
                Object.entries(value).forEach(([subKey, subValue]) => {
                    root.style.setProperty(
                        `--stream-${key}-${subKey}`,
                        subValue,
                    );
                });
            }
        });

        // Apply dark mode class to document
        if (isDarkMode) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }

        // Apply typography variables
        Object.entries(theme.typography).forEach(([key, value]) => {
            if (typeof value === "string") {
                root.style.setProperty(`--stream-typography-${key}`, value);
            } else if (typeof value === "object") {
                Object.entries(value).forEach(([subKey, subValue]) => {
                    root.style.setProperty(
                        `--stream-typography-${key}-${subKey}`,
                        subValue,
                    );
                });
            }
        });

        // Apply spacing variables
        Object.entries(theme.spacing).forEach(([key, value]) => {
            root.style.setProperty(`--stream-spacing-${key}`, value);
        });

        // Apply component variables
        Object.entries(theme.components).forEach(([key, value]) => {
            if (typeof value === "object") {
                Object.entries(value).forEach(([subKey, subValue]) => {
                    if (typeof subValue === "object") {
                        Object.entries(subValue).forEach(
                            ([subSubKey, subSubValue]) => {
                                root.style.setProperty(
                                    `--stream-${key}-${subKey}-${subSubKey}`,
                                    subSubValue,
                                );
                            },
                        );
                    } else {
                        root.style.setProperty(
                            `--stream-${key}-${subKey}`,
                            subValue,
                        );
                    }
                });
            }
        });

        // Apply stream-specific classes
        root.className = root.className.replace(/stream-\w+/g, "");
        root.classList.add(`stream-${activeStream}`);

        if (isDarkMode) {
            root.classList.add("dark-mode");
        } else {
            root.classList.remove("dark-mode");
        }

        // Apply accessibility settings
        if (accessibilitySettings.highContrast) {
            root.classList.add("high-contrast");
        } else {
            root.classList.remove("high-contrast");
        }

        if (accessibilitySettings.reducedMotion) {
            root.classList.add("reduced-motion");
        } else {
            root.classList.remove("reduced-motion");
        }

        if (accessibilitySettings.largeText) {
            root.classList.add("large-text");
        } else {
            root.classList.remove("large-text");
        }

        // Apply theme-specific accessibility settings
        if (theme.accessibility.highContrast) {
            root.classList.add("theme-high-contrast");
        }
        if (theme.accessibility.reducedMotion) {
            root.classList.add("theme-reduced-motion");
        }
        if (theme.accessibility.largeText) {
            root.classList.add("theme-large-text");
        }
        if (theme.accessibility.focusVisible) {
            root.classList.add("theme-focus-visible");
        }
    }, [theme, activeStream, isDarkMode, accessibilitySettings]);

    const contextValue: StreamThemeContextType = {
        theme,
        stream: activeStream,
        isDarkMode,
        toggleDarkMode,
        accessibilitySettings,
        updateAccessibilitySettings,
    };

    return (
        <StreamThemeContext.Provider value={contextValue}>
            {children}
        </StreamThemeContext.Provider>
    );
};

// ============================================================================
// HOOKS
// ============================================================================

export const useStreamTheme = (): StreamThemeContextType => {
    const context = useContext(StreamThemeContext);
    if (!context) {
        throw new Error(
            "useStreamTheme must be used within a StreamThemeProvider",
        );
    }
    return context;
};

export const useStreamColors = () => {
    const { theme } = useStreamTheme();
    return theme.colors;
};

export const useStreamTypography = () => {
    const { theme } = useStreamTheme();
    return theme.typography;
};

export const useStreamSpacing = () => {
    const { theme } = useStreamTheme();
    return theme.spacing;
};

export const useStreamComponents = () => {
    const { theme } = useStreamTheme();
    return theme.components;
};

// ============================================================================
// UTILITIES
// ============================================================================

export const getStreamTheme = (stream: StreamType): StreamTheme => {
    return streamThemes[stream];
};

export const getAllStreamThemes = (): Record<StreamType, StreamTheme> => {
    return streamThemes;
};

export default StreamThemeProvider;
